import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import { AUTH_USER_TOKEN_KEY, SELECTED_COMPANY_ID } from '../../../constants';
import { Logger } from '_clean-code/utils/logger';
import { IUserCompany } from 'types/user';

export type IUserCompanyResponse = IUserCompany[];

export const useUserCompanies = () => {
  const [selectedCompany, setSelectedCompany] = useState<
    IUserCompany | undefined
  >();

  const { data, isLoading, error } = useQuery<IUserCompanyResponse, Error>({
    queryKey: ['companies'],
    queryFn: async () => {
      const { data } = await api.get<IUserCompanyResponse>(
        '/identities/companies'
      );
      return data;
    },
    select: (data) =>
      // Remove duplicates
      data.filter((e, index, self) => self.indexOf(e) === index),
    enabled: !!localStorage.getItem(AUTH_USER_TOKEN_KEY),
    staleTime: Infinity, // Data will never become stale
    gcTime: Infinity // Data will never be garbage collected
  });

  const switchCompany = (company: IUserCompany) => {
    localStorage.setItem(SELECTED_COMPANY_ID, company.companyId.toString());
    setSelectedCompany(company);
    window.location.reload();
  };

  if (data) {
    const defaultCompanyId = data[0]?.companyId;
    const previousCompanyId = localStorage.getItem(SELECTED_COMPANY_ID);

    if (defaultCompanyId && !previousCompanyId) {
      // save companyId in storage if it's not already saved
      localStorage.setItem(SELECTED_COMPANY_ID, defaultCompanyId.toString());
      Logger.debug('useCompanies: saving company ID', defaultCompanyId);
    }

    if (selectedCompany === undefined) {
      // initialize selected company if it's not already set
      const previousCompany = data.find(
        (c) => c.companyId === parseInt(previousCompanyId ?? '0', 10)
      );
      setSelectedCompany(previousCompany ?? data[0]);
    }
  }

  return { data, isLoading, error, selectedCompany, switchCompany };
};
